/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import {
  recipientMastersService, type FunctionAttachment, type ActionAttachment,
} from '../../services/recipient-masters.service';
import { functionsService } from '../../services/functions.service';
import { actionMastersService } from '../../services/action-masters.service';
import { notificationMastersService } from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';
import { recipientListsService } from '../../services/recipient-lists.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import ActiveTag from '../../components/common/active-tag';
import { isRowActive } from '../../utils/active-state';
import type { IconName } from '../../components/common/icon';
import {
  CARD, BackLink, CardHeader, Field, Hero, ActiveBanner, RecordMeta,
} from '../../components/notifications/engine-view-ui';

/** A tinted icon per recipient kind, so the type reads at a glance. */
const TYPE_META: Record<string, { icon: IconName; chip: string }> = {
  LIST: { icon: 'users', chip: 'bg-indigo-50 text-indigo-500 dark:bg-indigo-950/50 dark:text-indigo-400' },
  FUNCTION: { icon: 'network', chip: 'bg-sky-50 text-sky-500 dark:bg-sky-950/50 dark:text-sky-400' },
  AI_AGENT: { icon: 'sparkles', chip: 'bg-amber-50 text-amber-500 dark:bg-amber-950/50 dark:text-amber-400' },
  USER: { icon: 'user', chip: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-950/50 dark:text-emerald-400' },
  PERSONNEL: { icon: 'id-card', chip: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-950/50 dark:text-emerald-400' },
  EXTERNAL: { icon: 'mail', chip: 'bg-violet-50 text-violet-500 dark:bg-violet-950/50 dark:text-violet-400' },
};

const RecipientMasterView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'recipient', basePath: '/recipient-masters' });
  const canUpdate = useCanUpdate(JOB_NAMES.RECIPIENT_MASTER);
  const toast = useRef<Toast>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recipientMaster', id],
    queryFn: () => recipientMastersService.getById(id!),
    enabled: isReady && !!id,
  });

  // Read only. Editing the chain and the actions belongs to the form, which
  // saves them with the rest of the recipient in one write.
  const chain: FunctionAttachment[] = data?.functions || [];
  const actions: ActionAttachment[] = data?.actions || [];

  const { data: listsPage } = useQuery({
    queryKey: ['recipientLists', 'recipientView'],
    queryFn: () => recipientListsService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });
  // The id is on the recipient; the code is what a person recognises.
  const listCode = (listId?: string | null): string =>
    (((listsPage as any)?.data || []).find((l: any) => l._id === String(listId))
      ?.recipientListCode) || '';

  // An attachment stores an id; a code is what a person recognises. Every list
  // is small and cached for the form, so these are lookups, not per-row fetches.
  const { data: functionsPage } = useQuery({
    queryKey: ['functions', 'recipientView'],
    queryFn: () => functionsService.list({ page: 1, page_size: 200 }),
    staleTime: 3e5,
  });
  const { data: actionsPage } = useQuery({
    queryKey: ['actionMasters', 'recipientView'],
    queryFn: () => actionMastersService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });
  // An action may fire another notification; that reference is an id, and a
  // notification has no code of its own, so it is read through its type.
  const { data: notifMastersPage } = useQuery({
    queryKey: ['notificationMasters', 'recipientView'],
    queryFn: () => notificationMastersService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });
  const { data: notifTypesPage } = useQuery({
    queryKey: ['notificationTypes', 'recipientView'],
    queryFn: () => notificationTypesService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });
  const codeOf = (page: any, wanted: any, field: string): string =>
    (page?.data || []).find((r: any) => r._id === String(wanted))?.[field] || String(wanted || '');
  const notificationLabel = (nid?: string | null): string => {
    const master = ((notifMastersPage as any)?.data || []).find((m: any) => m._id === String(nid));
    if (!master) return String(nid || '');
    return codeOf(notifTypesPage, master.notificationTypeId, 'notificationCode')
      || master.description || String(nid);
  };

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) return (
    <div className="p-4">
      <Message severity="error" text={extractErrorMessage(error, 'Recipient not found')} className="mb-4 w-full" />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
    </div>
  );

  const editable = canUpdate && !data.isDelete;
  const meta: { icon: IconName; chip: string } = TYPE_META[data.recipientType]
    || { icon: 'user', chip: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300' };

  return (
    <div className="mx-auto space-y-3 p-4" data-testid="SCR-RecipientMaster-View">
      <Toast ref={toast} position="top-right" />
      <BackLink label="Recipients" onClick={() => navigateToList()} />

      <Hero
        icon={meta.icon}
        chip={meta.chip}
        code={data.recipientCode}
        tags={<>
          <Tag value={data.recipientType} severity={data.recipientType === 'FUNCTION' ? 'info' : data.recipientType === 'AI_AGENT' ? 'warning' : 'secondary'} className="text-xs px-1.5 py-0.5" />
          <ActiveTag row={data} />
        </>}
        description={data.description}
        actions={editable && <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" onClick={() => id && navigateToEdit(id)} />}
        banner={isRowActive(data) && <ActiveBanner text="Active — edits apply from the next fire; messages already sent are unchanged." />}
      />

      {data.recipientType === 'LIST' && (
        <div className={CARD}>
          <CardHeader icon="users" color="text-indigo-400" title="Recipient list" />
          <div className="px-4 py-4">
            {listCode((data as any).recipientListId)
              ? <span className="font-mono text-sm text-ink">{listCode((data as any).recipientListId)}</span>
              : <span className="text-sm italic text-gray-400">Not set</span>}
          </div>
        </div>
      )}

      {data.recipientType === 'AI_AGENT' && (
        <Message severity="warn" className="w-full"
          text="Not built yet. It reaches nobody, and the fire says so." />
      )}

      {data.recipientType === 'FUNCTION' && (
        <div className={CARD} data-testid="RecipientMasterView.Chain">
          <CardHeader icon="network" color="text-sky-400" title="Function chain" hint="Runs in order · edit from Edit" />
          <div className="px-4 py-4">
            {chain.length === 0 ? (
              <p className="text-sm text-gray-400">None — this recipient can never find anybody.</p>
            ) : (
              <ol className="flex flex-col gap-2">
                {chain.map((f, index) => {
                  const inputs = Object.entries(f.inputMapping || {});
                  const outputs = Object.entries(f.outputMapping || {});
                  return (
                    <li key={`${(f as any).functionMasterId}-${index}`} className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900/30">
                      <div className="flex items-center gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-600 dark:bg-sky-950/50 dark:text-sky-400">{index + 1}</span>
                        <span className="font-mono text-sm text-ink">
                          {codeOf(functionsPage, (f as any).functionMasterId, 'functionCode')}
                        </span>
                        {(f as any).timeoutSeconds
                          ? <span className="ml-auto text-xs text-gray-400">{(f as any).timeoutSeconds}s timeout</span>
                          : null}
                      </div>
                      {(inputs.length > 0 || outputs.length > 0) && (
                        <div className="mt-2 grid gap-3 pl-9 sm:grid-cols-2">
                          {inputs.length > 0 && (
                            <div>
                              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Inputs</div>
                              <ul className="flex flex-col gap-0.5">
                                {inputs.map(([field, srcSpec]) => (
                                  <li key={field} className="text-xs">
                                    <span className="font-mono text-gray-700 dark:text-gray-200">{field}</span>
                                    <span className="text-gray-400"> ← </span>
                                    <span className="font-mono text-ink-muted">
                                      {srcSpec?.value !== undefined
                                        ? JSON.stringify(srcSpec.value)
                                        : `${srcSpec?.source ?? ''}${srcSpec?.path ? `.${srcSpec.path}` : ''}`}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {outputs.length > 0 && (
                            <div>
                              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Outputs</div>
                              <ul className="flex flex-col gap-0.5">
                                {outputs.map(([alias, srcKey]) => (
                                  <li key={alias} className="text-xs">
                                    <span className="font-mono text-gray-700 dark:text-gray-200">{alias}</span>
                                    <span className="text-gray-400"> ← </span>
                                    <span className="font-mono text-ink-muted">{String(srcKey)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}

      <div className={CARD} data-testid="RecipientMasterView.Actions">
        <CardHeader icon="zap" color="text-amber-400" title="Actions" hint="Edit from Edit" />
        <div className="px-4 py-4">
          {actions.length === 0 ? (
            <p className="text-sm text-gray-400">None — recipients can read the notification but not respond.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {actions.map((a, index) => {
                const allowed: string[] = (a as any).allowedFromStates || [];
                const target = (a as any).targetState;
                const firesId = (a as any).notificationMasterId;
                return (
                  <li key={`${(a as any).actionMasterId}-${index}`} className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900/30">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm text-ink">
                        {codeOf(actionsPage, (a as any).actionMasterId, 'actionCode')}
                      </span>
                      {(a as any).isDefault && <Tag value="Default" severity="info" className="text-xs px-1.5 py-0.5" />}
                      {target && (
                        <span className="text-xs text-ink-muted">
                          moves to <span className="font-mono text-gray-700 dark:text-gray-200">{target}</span>
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
                      <span>
                        From:{' '}
                        {allowed.length > 0
                          ? <span className="font-mono text-gray-700 dark:text-gray-200">{allowed.join(', ')}</span>
                          : <span className="italic">any state</span>}
                      </span>
                      {firesId && (
                        <span>Fires: <span className="font-mono text-gray-700 dark:text-gray-200">{notificationLabel(firesId)}</span></span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <RecordMeta data={data} extra={<Field label="Type">{data.recipientType}</Field>} />
    </div>
  );
};

export default RecipientMasterView;
